import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, BarChart3, Calendar, Youtube, FileText, Settings, Swords, ChevronDown, ChevronUp } from 'lucide-react';
import SubscribeButton from '../components/SubscribeButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAV_LINKS = [
  { href: '/', label: 'HOME' },
  { href: '/votar', label: '🗳️ VOTAR' },
  { href: '/estatisticas', label: 'ESTATÍSTICAS', icon: BarChart3 },
  { href: '/partidas', label: 'PARTIDAS', icon: Calendar },
  { href: '/torneios', label: 'TORNEIOS', icon: Swords, active: true },
  { href: '/rankings', label: 'RANKINGS', icon: Trophy },
  { href: '/replays', label: 'REPLAYS', icon: Youtube },
  { href: '/regras', label: 'REGRAS', icon: FileText },
];

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('pt-PT', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return iso; }
};

const STAT_LABEL = {
  goals: '⚽ Gols', assists: '🎯 Assist.', saves: '🧤 Defesas',
  tackles: '🦵 Desarmes', blocks: '🧱 Bloqueios', shots_on_target: '🎯 Rem. Alvo',
  shots_missed: '❌ Rem. Fora', passes_completed: '✅ Passes', passes_failed: '⛔ Passes Err.',
  fouls_committed: '🚫 Faltas', own_goals: '🥅 Gols Contra', goals_conceded: '🥅 Gols Sofridos',
  clean_sheets: '🛡️ JSG', dribbles: '💫 Dribles', interceptions: '✋ Intercept.',
  possession_lost: '📉 Posse Perd.', control_errors: '⚠️ Err. Controle', throws: '🎯 Lançam.',
  wins: '🏆 V', draws: '🤝 E', losses: '❌ D', games_played: '🎮 Jogos',
};

const TorneiosPage = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [openMatch, setOpenMatch] = useState({}); // per-day per-match expand

  useEffect(() => {
    axios.get(`${API}/torneios?limit=500`)
      .then(r => setHistory(r.data.history || []))
      .catch(err => console.error('Erro ao carregar torneios:', err))
      .finally(() => setLoading(false));
  }, []);

  // Group matches by date
  const byDate = {};
  for (const m of history) {
    const k = m.date || 'sem-data';
    if (!byDate[k]) byDate[k] = [];
    byDate[k].push(m);
  }
  const dates = Object.keys(byDate).sort().reverse(); // newest first

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="torneios-page">
      <header className="bg-zinc-950 text-white py-4 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tighter">⚽ DOMINGÃO</h1>
          <div className="flex items-center gap-2">
            <SubscribeButton />
            <a href="/admin" className="text-sm font-bold hover:text-green-400 transition-colors" data-testid="go-to-admin">
              <Settings size={24} />
            </a>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b-2 border-zinc-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {NAV_LINKS.map(l => {
              const Icon = l.icon;
              const cls = l.active ? 'bg-green-500 text-white' : 'bg-zinc-100 hover:bg-zinc-200 transition-colors';
              return (
                <a key={l.href} href={l.href} className={`flex-shrink-0 ${cls} px-4 py-2 rounded-lg font-bold text-sm`} data-testid={`nav-${l.href.replace('/', '') || 'home'}`}>
                  {Icon && <Icon className="inline mr-1" size={16} />} {l.label}
                </a>
              );
            })}
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h2 className="text-2xl font-black tracking-tight uppercase flex items-center gap-2">
            <Swords size={28} className="text-green-500" /> TORNEIOS
          </h2>
          <p className="text-zinc-500 text-sm mt-1">Resumo das partidas ao vivo, agrupado por dia de jogo</p>
        </div>

        {loading ? (
          <p className="text-center text-zinc-500 py-8">A carregar torneios…</p>
        ) : dates.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-zinc-200 p-8 text-center text-zinc-500">
            Ainda não há partidas ao vivo registadas. Quando o admin terminar um jogo pela aba &quot;Jogo Ao Vivo&quot;, ele aparece aqui.
          </div>
        ) : (
          <div className="space-y-4">
            {dates.map(date => {
              const dayMatches = byDate[date];
              const isOpen = expanded[date] !== false; // default open
              return (
                <div key={date} className="bg-white rounded-xl border-2 border-zinc-200 overflow-hidden" data-testid={`torneio-day-${date}`}>
                  <button
                    onClick={() => setExpanded(prev => ({ ...prev, [date]: !isOpen }))}
                    className="w-full flex items-center justify-between p-4 bg-zinc-900 text-white hover:bg-zinc-800 transition-colors"
                  >
                    <div className="text-left">
                      <p className="text-xs uppercase tracking-widest text-green-400">Torneio do Dia</p>
                      <p className="text-lg sm:text-xl font-black uppercase">{formatDate(date)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="bg-yellow-500 text-black px-3 py-1 rounded-full text-xs font-black">
                        {dayMatches.length} PARTIDA{dayMatches.length > 1 ? 'S' : ''}
                      </span>
                      {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="p-3 space-y-3">
                      {dayMatches.map((m, idx) => (
                        <MatchCard
                          key={m.id || idx}
                          match={m}
                          idx={idx}
                          isOpen={openMatch[`${date}_${idx}`]}
                          onToggle={() => setOpenMatch(prev => ({ ...prev, [`${date}_${idx}`]: !prev[`${date}_${idx}`] }))}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

const MatchCard = ({ match, idx, isOpen, onToggle }) => {
  const winnerName = match.winner === 'a' ? match.team_a_name
    : match.winner === 'b' ? match.team_b_name : null;
  return (
    <div className="border-2 border-zinc-200 rounded-lg overflow-hidden" data-testid={`torneio-match-${idx}`}>
      <button onClick={onToggle} className="w-full p-3 hover:bg-zinc-50 transition-colors text-left">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Partida {idx + 1}{match.name ? ` · ${match.name}` : ''}</p>
            <p className="text-lg sm:text-xl font-black tabular-nums">
              <span className={match.winner === 'a' ? 'text-green-700' : 'text-zinc-700'}>{match.team_a_name}</span>
              <span className="mx-2 text-zinc-400">{match.score_a} - {match.score_b}</span>
              <span className={match.winner === 'b' ? 'text-green-700' : 'text-zinc-700'}>{match.team_b_name}</span>
            </p>
            {winnerName ? (
              <p className="text-xs font-bold text-green-700">🏆 {winnerName} venceu</p>
            ) : (
              <p className="text-xs font-bold text-orange-600">🤝 Empate</p>
            )}
          </div>
          {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </button>

      {isOpen && (
        <div className="border-t-2 border-zinc-200 p-3 bg-zinc-50 space-y-3">
          {/* Highlights */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {match.top_scorer && (
              <div className="bg-red-50 border-2 border-red-300 rounded-lg p-2">
                <p className="text-[10px] font-black uppercase text-red-700">Artilheiro</p>
                <p className="font-black text-sm truncate">{match.top_scorer.name}</p>
                <p className="text-xs">{match.top_scorer.value} gol(s)</p>
              </div>
            )}
            {match.top_assist && (
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-2">
                <p className="text-[10px] font-black uppercase text-blue-700">Garçom</p>
                <p className="font-black text-sm truncate">{match.top_assist.name}</p>
                <p className="text-xs">{match.top_assist.value} assist.</p>
              </div>
            )}
            {match.top_saves && (
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-2">
                <p className="text-[10px] font-black uppercase text-green-700">Muralha</p>
                <p className="font-black text-sm truncate">{match.top_saves.name}</p>
                <p className="text-xs">{match.top_saves.value} defesa(s)</p>
              </div>
            )}
          </div>

          {/* Clean sheets */}
          {match.clean_sheet_names?.length > 0 && (
            <div className="border-2 border-emerald-500 bg-emerald-50 rounded-lg p-2">
              <p className="text-[10px] font-black uppercase text-emerald-700">🧤 Não Sofreram Golo</p>
              <p className="text-xs font-semibold text-emerald-900">{match.clean_sheet_names.join(' · ')}</p>
            </div>
          )}

          {/* Escalação (starting lineup per team) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {['team_a', 'team_b'].map(side => (
              <div key={side} className={`border-2 rounded-lg p-2 ${side === 'team_a' ? 'border-red-400 bg-red-50/40' : 'border-blue-400 bg-blue-50/40'}`}>
                <p className={`text-xs font-black uppercase mb-1 ${side === 'team_a' ? 'text-red-700' : 'text-blue-700'}`}>
                  {match[`${side}_name`]} · Escalação
                </p>
                <ul className="text-xs space-y-0.5">
                  {(match[side] || []).map(pl => (
                    <li key={pl.name} className="flex justify-between">
                      <span className="font-bold">{pl.name}</span>
                      <span className="text-zinc-500 text-[10px] uppercase">{pl.slot}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Full player-by-player stat breakdown */}
          {match.per_player_summary && Object.keys(match.per_player_summary).length > 0 && (
            <details className="border-2 border-zinc-300 rounded-lg">
              <summary className="cursor-pointer px-3 py-2 font-black uppercase text-xs text-zinc-700 bg-zinc-100">
                Estatísticas Detalhadas por Jogador
              </summary>
              <div className="p-2 text-xs max-h-64 overflow-y-auto">
                {Object.entries(match.per_player_summary).map(([name, s]) => (
                  <div key={name} className="flex justify-between border-b border-zinc-200 py-1 gap-2">
                    <span className="font-bold whitespace-nowrap">{name} <span className="text-zinc-400 font-normal">({s.slot})</span></span>
                    <span className="text-zinc-600 text-right tabular-nums">
                      {Object.entries(s).filter(([k, v]) => !['team', 'slot'].includes(k) && Number(v) > 0)
                        .map(([k, v]) => `${STAT_LABEL[k] || k}:${v}`).join(' · ')}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default TorneiosPage;
